import { memo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../../../components/AuraLayout";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { useStore } from "../../../store/globalState";
import { errorInitialState } from "../../../constants";
import type { IErrorState } from "../interfaces/error";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { getEvents, safeCapture } from "../../../analytics";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

export const ErrorPage = memo(() => {
  const { t } = useTranslation();
  const { theme, errorState } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  const error =
    (location.state as IErrorState) || errorState || errorInitialState;
  const {
    title: errorTitle,
    subTitle = "",
    message,
    btnText,
    isOut,
    pageUrl,
    returnTo,
    errorCode,
  } = error;

  useEffect(() => {
    safeCapture(getEvents().ERROR_SCREEN, {
      error_code: errorCode,
    });
  }, [errorCode]);

  const handleRetry = () => {
    safeCapture(getEvents().EXIT_CLICK, { screen: "error" });
    const isExternalUrl =
      typeof pageUrl === "string" &&
      (pageUrl.startsWith("http://") || pageUrl.startsWith("https://"));

    if ((isOut || isExternalUrl) && pageUrl) {
      window.location.href = `${pageUrl}`;
      return;
    }

    if (typeof pageUrl === "string" && pageUrl.length > 0) {
      navigate(pageUrl, {
        replace: true,
        ...(returnTo ? { state: { returnTo } } : {}),
      });
      return;
    }
    navigate(-1);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />

        <section className="flow-hero">

          <FlowHeroMark backgroundIcon={backgroundIcon}>

            <ErrorIcon size={60} colors={iconColors} />

          </FlowHeroMark>
        <section className="landing-container">
          <PageTitle
            highlight={errorTitle || t("error.title")}
            highlightColor={title}
            secudnary={subTitle || undefined}
            secudnaryColor={title}
          />
          <BodyCopy textColor={bodyCopy}>
            {message || t("error.tryAgainLater")}
          </BodyCopy>
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {(btnText || pageUrl) && (
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleRetry}
            >
              {btnText || t("common.retry")}
            </Button>
          )}
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
